import CardRule from "./Types/CardRule";
import GameRule from "./Types/GameRule";
import Direction from "./Types/Direction";
import Pile from "./Types/Pile";
import PileType from "./Types/Pile";
import PlayedCard from "./Types/PlayedCard";
import moment from "moment";
import Game from "./Types/Game";
import _insane_rules from "./rules/insane_rules.json";
import _hard_rules from "./rules/hard_rules.json";
import { Rules } from "./Types/Rules";

const hardRules = _hard_rules as unknown as Record<string, Rules>;
const insaneRules = _insane_rules as unknown as Record<string, Rules>;

const getBombCards = () => [22, 33, 44, 55, 66, 77];

type HardOrInsane = GameRule.Hard | GameRule.Insane;

const getCardsByRule = (
  rule: keyof Rules,
  gameId: number,
  gameRule: GameRule
) => {
  const ruleId = gameId % 100;

  return gameRule === GameRule.Hard
    ? hardRules[0][rule]
    : insaneRules[ruleId][rule];
};
const getStopCards = (gameId: number, gameRule: HardOrInsane) =>
  getCardsByRule("stop", gameId, gameRule);
const getThreeCards = (gameId: number, gameRule: HardOrInsane) =>
  getCardsByRule("three", gameId, gameRule);
const getSinglePileCards = (gameId: number, gameRule: HardOrInsane) =>
  getCardsByRule("singlePile", gameId, gameRule);
const getSkullCards = (gameId: number, gameRule: HardOrInsane) =>
  getCardsByRule("skull", gameId, gameRule);
const getNoSpeechCards = (gameId: number, gameRule: HardOrInsane) =>
  getCardsByRule("noSpeech", gameId, gameRule);
const getDrawOnlyOneCards = (gameId: number, gameRule: HardOrInsane) =>
  getCardsByRule("drawOnlyOne", gameId, gameRule);
const getNoMagicCards = (gameId: number, gameRule: HardOrInsane) =>
  getCardsByRule("noMagic", gameId, gameRule);

const getCardRule = (
  number: number,
  gameRule: GameRule,
  gameId: Game["id"]
) => {
  if (gameRule === GameRule.Bomb) {
    return getBombCards().some((n) => n === number)
      ? CardRule.Bomb
      : CardRule.None;
  } else if (gameRule === GameRule.Hard) {
    if (getNoMagicCards(0, gameRule).some((n) => n === number))
      return CardRule.NoMagic;

    if (getStopCards(0, gameRule).some((n) => n === number))
      return CardRule.Stop;

    if (getSinglePileCards(0, gameRule).some((n) => n === number))
      return CardRule.SinglePile;

    if (getThreeCards(0, gameRule).some((n) => n === number))
      return CardRule.MustPlaceThreeCards;

    if (getSkullCards(0, gameRule).some((n) => n === number))
      return CardRule.Skull;

    if (getNoSpeechCards(0, gameRule).some((n) => n === number))
      return CardRule.NoSpeech;

    if (getDrawOnlyOneCards(0, gameRule).some((n) => n === number))
      return CardRule.DrawOnlyOneCard;
  } else if (gameRule === GameRule.Insane) {
    if (getNoMagicCards(gameId, gameRule).some((n) => n === number))
      return CardRule.NoMagic;

    if (getStopCards(gameId, gameRule).some((n) => n === number))
      return CardRule.Stop;

    if (getSinglePileCards(gameId, gameRule).some((n) => n === number))
      return CardRule.SinglePile;

    if (getThreeCards(gameId, gameRule).some((n) => n === number))
      return CardRule.MustPlaceThreeCards;

    if (getSkullCards(gameId, gameRule).some((n) => n === number))
      return CardRule.Skull;

    if (getNoSpeechCards(gameId, gameRule).some((n) => n === number))
      return CardRule.NoSpeech;

    if (getDrawOnlyOneCards(gameId, gameRule).some((n) => n === number))
      return CardRule.DrawOnlyOneCard;
  }

  return CardRule.None;
};

const getPilesRules = (gameRule: GameRule, piles: Pile[], gameId: number) => {
  return piles.map((n) => getCardRule(n.number, gameRule, gameId));
};

const isRuleDisallowed = (
  gameRule: GameRule,
  cardRule: CardRule,
  piles: Pile[],
  gameId: number
) => {
  return getPilesRules(gameRule, piles, gameId).some(
    (rule) => rule === cardRule
  );
};

const LEGACY_convertToPiles = (playedCards: PlayedCard[]) => {
  let cards = [...playedCards];

  let pile1 = cards.filter((c) => c.pileIndex === 1).pop();
  let pile2 = cards.filter((c) => c.pileIndex === 2).pop();
  let pile3 = cards.filter((c) => c.pileIndex === 3).pop();
  let pile4 = cards.filter((c) => c.pileIndex === 4).pop();

  let piles: Pile[] = [
    {
      number: pile1?.playedCard || Direction.Up,
      direction: Direction.Up,
      index: pile1?.pileIndex || 1,
    },
    {
      number: pile2?.playedCard || Direction.Up,
      direction: Direction.Up,
      index: pile2?.pileIndex || 2,
    },
    {
      number: pile3?.playedCard || Direction.Down,
      direction: Direction.Down,
      index: pile3?.pileIndex || 3,
    },
    {
      number: pile4?.playedCard || Direction.Down,
      direction: Direction.Down,
      index: pile4?.pileIndex || 4,
    },
  ];

  return piles;
};

const LEGACY_convertCardsPlayedByUser = (
  cardsPlacedInThisRound: { playedCard: number; pileIndex: number }[]
) => {
  return cardsPlacedInThisRound.map((card) => {
    let pile: Pile = {
      direction: card.pileIndex <= 2 ? Direction.Up : Direction.Down,
      number: card.playedCard,
      index: card.pileIndex,
    };

    return { pile, number: card.playedCard };
  });
};

const getIsDrawOnlyOneCardOnTopInAnyPile = (
  gameRule: GameRule,
  piles: Pile[],
  gameId: number
) => {
  const isDrawOnlyOneCardOnTopInAnyPile = piles?.some((card) => {
    let cardRule = getCardRule(card.number, gameRule, gameId);

    return cardRule === CardRule.DrawOnlyOneCard;
  });

  log("isDrawOnlyOneCardOnTopInAnyPile", isDrawOnlyOneCardOnTopInAnyPile);

  return isDrawOnlyOneCardOnTopInAnyPile;
};

const getIsEndTurnButtonEnabled = (
  gameRule: GameRule,
  piles: Pile[],
  numberOfCardsInDeck: number,
  gameId: number,
  cardsPlacedInThisRound?: { pile: Pile; number: number }[]
) => {
  if (gameRule === GameRule.Hard || gameRule === GameRule.Insane) {
    let stopCardHasBeenPlayedInThisTurn =
      validateStopCards(piles, gameRule, gameId, cardsPlacedInThisRound)
        .length === 0;
    let threeCardHaveBeenPlayedAndThreeCardHaveBeenPlayed =
      validateMustPlaceThreeCards(
        piles,
        gameRule,
        gameId,
        undefined,
        cardsPlacedInThisRound
      ).length === 0;

    let cardsOnTop = piles.map((p) => p.number);

    let skullCardShowing = cardsOnTop.some((c) =>
      getSkullCards(gameId, gameRule).includes(c)
    );

    let threeCardWasPlacedInThisTurn = cardsPlacedInThisRound?.some(
      (card) =>
        getCardRule(card.number, gameRule, gameId) ===
        CardRule.MustPlaceThreeCards
    );

    if (
      skullCardShowing ||
      (threeCardWasPlacedInThisTurn &&
        !threeCardHaveBeenPlayedAndThreeCardHaveBeenPlayed)
    ) {
      return false;
    }

    if (stopCardHasBeenPlayedInThisTurn) {
      return true;
    }
  }

  return (
    cardsPlacedInThisRound &&
    cardsPlacedInThisRound.length >=
      (numberOfCardsInDeck !== undefined && numberOfCardsInDeck > 0 ? 2 : 1)
  );
};

const getNormallyAvailablePiles = (
  piles: Pile[],
  pendingCard: number,
  includeMagic: boolean
) => {
  return piles.filter((pile) => {
    if (pile.direction === Direction.Up) {
      return (
        pendingCard > pile.number ||
        (includeMagic && Math.abs(pile.number - pendingCard) === 10)
      );
    } else {
      return (
        pendingCard < pile.number ||
        (includeMagic && Math.abs(pile.number - pendingCard) === 10)
      );
    }
  });
};

const validateNoMagicCards = (
  piles: Pile[],
  normallyAvailablePiles: Pile[],
  pendingCard: number,
  gameRule: GameRule,
  gameId: number
) => {
  let magic10CompatiblePiles = normallyAvailablePiles.filter(
    (pile) => Math.abs(pile.number - pendingCard) === 10
  );

  if (magic10CompatiblePiles.length > 0) {
    let isMagic10Allowed = !isRuleDisallowed(
      gameRule,
      CardRule.NoMagic,
      piles,
      gameId
    );

    return isMagic10Allowed
      ? normallyAvailablePiles
      : getNormallyAvailablePiles(piles, pendingCard, false);
  }

  return normallyAvailablePiles;
};

const validateStopCards = (
  normallyAvailablePiles: Pile[],
  gameRule: GameRule,
  gameId: number,
  cardsPlacedInThisRound?: { pile: Pile; number: number }[]
) => {
  let threeCardHasBeenPlayed = cardsPlacedInThisRound?.some(
    (card) =>
      getCardRule(card.number, gameRule, gameId) ===
      CardRule.MustPlaceThreeCards
  );

  return cardsPlacedInThisRound?.some((card) => {
    let cardRule = getCardRule(card.number, gameRule, gameId);

    return cardRule === CardRule.Stop;
  }) ||
    (threeCardHasBeenPlayed &&
      cardsPlacedInThisRound &&
      cardsPlacedInThisRound?.length > 2)
    ? []
    : normallyAvailablePiles;
};

const validateMustPlaceThreeCards = (
  normallyAvailablePiles: Pile[],
  gameRule: GameRule,
  gameId: number,
  pendingCard?: number,
  cardsPlacedInThisRound?: { pile: Pile; number: number }[]
) => {
  let threeCardHasBeenPlayed = cardsPlacedInThisRound?.some(
    (card) =>
      getCardRule(card.number, gameRule, gameId) ===
      CardRule.MustPlaceThreeCards
  );

  let pendingCardRule = pendingCard
    ? getCardRule(pendingCard, gameRule, gameId)
    : CardRule.None;

  let numberOfCardsInThisRound =
    (cardsPlacedInThisRound && cardsPlacedInThisRound?.length) || 0;

  return (threeCardHasBeenPlayed && numberOfCardsInThisRound >= 3) ||
    (!threeCardHasBeenPlayed &&
      numberOfCardsInThisRound >= 3 &&
      pendingCardRule === CardRule.MustPlaceThreeCards)
    ? []
    : normallyAvailablePiles;
};

const validateSinglePileCards = (
  piles: Pile[],
  normallyAvailablePiles: Pile[],
  gameRule: GameRule,
  gameId: number,
  cardsPlacedInThisRound?: { pile: Pile; number: number }[]
) => {
  if (cardsPlacedInThisRound && cardsPlacedInThisRound.length > 0) {
    let previousCardPlacedByUser =
      cardsPlacedInThisRound[cardsPlacedInThisRound.length - 1];

    let isSinglePilePresent = isRuleDisallowed(
      gameRule,
      CardRule.SinglePile,
      piles,
      gameId
    );

    if (isSinglePilePresent && previousCardPlacedByUser) {
      return [previousCardPlacedByUser.pile];
    } else {
      return normallyAvailablePiles;
    }
  } else {
    return normallyAvailablePiles;
  }
};

const validateSkullCards = (
  normallyAvailablePiles: Pile[],
  gameRule: GameRule,
  gameId: number,
  cardsPlacedInThisRound?: { pile: Pile; number: number }[]
) => {
  let previousCardPlacedByUser = cardsPlacedInThisRound
    ? cardsPlacedInThisRound[cardsPlacedInThisRound.length - 1]
    : null;

  let previousCardPlacedByUserWasSkull =
    previousCardPlacedByUser &&
    getCardRule(previousCardPlacedByUser.number, gameRule, gameId) ===
      CardRule.Skull;

  if (previousCardPlacedByUserWasSkull && previousCardPlacedByUser) {
    return [previousCardPlacedByUser.pile];
  } else {
    return normallyAvailablePiles;
  }
};

const log = (message: string, params: any) => {
  // console.log(message, params);
};

const getIsSpeakingAllowed = (
  gameRule: GameRule,
  gameId: number,
  piles: Pile[]
) => {
  if (gameRule === GameRule.Hard) {
    return !isRuleDisallowed(gameRule, CardRule.NoSpeech, piles, gameId);
  }

  return true;
};

const getAvailablePilesPerCard = (
  gameRule: GameRule,
  piles: Pile[],
  cardsOnHand: number[],
  gameId: number,
  cardsPlacedInThisRound?: { pile: Pile; number: number }[]
) =>
  cardsOnHand.reduce<{ [c: number]: Pile[] }>((dict, c) => {
    dict[c] = getAvailablePiles(
      gameRule,
      piles,
      c,
      gameId,
      cardsPlacedInThisRound
    );
    return dict;
  }, {});

const getAvailablePiles = (
  gameRule: GameRule,
  piles: Pile[],
  pendingCard: number,
  gameId: number,
  cardsPlacedInThisRound?: { pile: Pile; number: number }[]
) => {
  let normallyAvailablePiles = getNormallyAvailablePiles(
    piles,
    pendingCard,
    true
  );

  log("utils", piles);

  if (gameRule === GameRule.Normal) {
    return normallyAvailablePiles;
  }

  if (gameRule === GameRule.Bomb) {
    let bombCardShowing = piles.some(
      (pile) => getCardRule(pile.number, gameRule, gameId) === CardRule.Bomb
    );

    return bombCardShowing
      ? normallyAvailablePiles.filter(
          (pile) => getCardRule(pile.number, gameRule, gameId) === CardRule.Bomb
        )
      : normallyAvailablePiles;
  }

  if (gameRule === GameRule.Hard || gameRule === GameRule.Insane) {
    let pilesMagic10Validated = validateNoMagicCards(
      piles,
      normallyAvailablePiles,
      pendingCard,
      gameRule,
      gameId
    );

    let pilesStopCardsValidated = validateStopCards(
      normallyAvailablePiles,
      gameRule,
      gameId,
      cardsPlacedInThisRound
    );

    let pilesMustPlaceThreeCardsValidated = validateMustPlaceThreeCards(
      normallyAvailablePiles,
      gameRule,
      gameId,
      pendingCard,
      cardsPlacedInThisRound
    );

    let pilesSinglePileCardsValidate = validateSinglePileCards(
      piles,
      normallyAvailablePiles,
      gameRule,
      gameId,
      cardsPlacedInThisRound
    );

    let pilesSkullCardsValidate = validateSkullCards(
      normallyAvailablePiles,
      gameRule,
      gameId,
      cardsPlacedInThisRound
    );

    let allowedPiles: Pile[] = [];

    allowedPiles = [
      ...normallyAvailablePiles,
      ...pilesMagic10Validated,
      ...pilesStopCardsValidated,
      ...pilesMustPlaceThreeCardsValidated,
      ...pilesSinglePileCardsValidate,
      ...pilesSkullCardsValidate,
    ];

    log("utils: normallyAvailablePiles: ", normallyAvailablePiles);
    log("utils: pilesMagic10Validated: ", pilesMagic10Validated);
    log("utils: pilesStopCardsValidated: ", pilesStopCardsValidated);
    log(
      "utils: pilesMustPlaceThreeCardsValidated: ",
      pilesMustPlaceThreeCardsValidated
    );
    log("utils: pilesSinglePileCardsValidate: ", pilesSinglePileCardsValidate);
    log("utils: pilesSkullCardsValidate: ", pilesSkullCardsValidate);

    let numberOfRules = 6;
    let allowedByAllRules = [];

    if (
      allowedPiles.filter((p) => piles[0].index === p.index).length ===
      numberOfRules
    ) {
      allowedByAllRules.push(piles[0]);
    }
    if (
      allowedPiles.filter((pile) => piles[1].index === pile.index).length ===
      numberOfRules
    ) {
      allowedByAllRules.push(piles[1]);
    }
    if (
      allowedPiles.filter((pile) => piles[2].index === pile.index).length ===
      numberOfRules
    ) {
      allowedByAllRules.push(piles[2]);
    }
    if (
      allowedPiles.filter((pile) => piles[3].index === pile.index).length ===
      numberOfRules
    ) {
      allowedByAllRules.push(piles[3]);
    }

    log("utils, allowedByAllRules: ", allowedByAllRules);

    return allowedByAllRules;
  }

  return piles;
};

const isGameOver = (
  availableCards: { [c: number]: PileType[] },
  isEndTurnButtonEnabled: boolean = false,
  isPlayersTurn: boolean = false
) => {
  if (
    isPlayersTurn &&
    availableCards &&
    Object.values(availableCards).flatMap((pile) => pile).length === 0
  ) {
    if (!isEndTurnButtonEnabled) {
      return true;
    }
  }
  return false;
};

const isOldGame = (game: Game) =>
  moment(game.created) <= moment().add(-1, "day");

export {
  getAvailablePilesPerCard,
  getAvailablePiles,
  getCardRule,
  getBombCards,
  getStopCards,
  getSinglePileCards,
  getThreeCards,
  getSkullCards,
  getIsEndTurnButtonEnabled,
  LEGACY_convertToPiles,
  LEGACY_convertCardsPlayedByUser,
  getIsDrawOnlyOneCardOnTopInAnyPile,
  getIsSpeakingAllowed,
  isGameOver,
  isOldGame,
};
